import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Store } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiGet, apiPatch } from "@/services/api";

export function VendorSettingsPage() {
  const [form, setForm] = useState({
    businessName: "",
    ownerName: "",
    email: "",
    phone: "",
    address: "",
    description: "",
    logo: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void apiGet<{ vendor: Record<string, string | null> }>("/api/vendors/me").then((d) => {
      if (!d?.vendor) return;
      setForm({
        businessName: d.vendor.businessName ?? "",
        ownerName: d.vendor.ownerName ?? "",
        email: d.vendor.email ?? "",
        phone: d.vendor.phone ?? "",
        address: d.vendor.address ?? "",
        description: d.vendor.description ?? "",
        logo: d.vendor.logo ?? "",
      });
    });
  }, []);

  async function save() {
    setSaving(true);
    try {
      await apiPatch("/api/vendors/me", form);
      toast.success("Store settings updated successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
          Store Profile & Settings
        </h1>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Configure your public brand appearance, contact info, and business location.
        </p>
      </div>

      <div className="amazon-card p-4 sm:p-7 space-y-5">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
          <Store className="h-5 w-5 text-[#087DB5]" />
          <h2 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
            Commercial Store Identity
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Business Name */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Commercial Business Name <span className="text-rose-500">*</span>
            </label>
            <input
              value={form.businessName}
              onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              placeholder="e.g. Acme Electronics Store"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Owner Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Representative / Owner Name
            </label>
            <input
              value={form.ownerName}
              onChange={(e) => setForm((f) => ({ ...f, ownerName: e.target.value }))}
              placeholder="Full Name"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Contact Phone Number
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="e.g. +251 91 234 5678"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Business Contact Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="store@domain.com"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Store Physical Address
            </label>
            <input
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="e.g. Bole Medhanialem, Addis Ababa"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>

          {/* Store Logo */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Store Logo Image URL
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="url"
                value={form.logo}
                onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
                placeholder="https://example.com/logo.png"
                className="h-10 flex-1 rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
              {form.logo && (
                <div className="h-10 w-10 shrink-0 rounded-xl border border-slate-200 bg-white p-1 overflow-hidden flex items-center justify-center dark:border-white/10 dark:bg-slate-800">
                  <img
                    src={form.logo}
                    alt="Logo preview"
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Store Public Description
            </label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Brief overview of the products and brands your store specializes in…"
              className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50/60 p-3 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div className="pt-2">
          <Button
            disabled={saving}
            onClick={() => void save()}
            className="w-full sm:w-auto h-10 px-6 gap-2 bg-[#087DB5] hover:bg-[#073B70] text-white font-bold text-xs sm:text-sm shadow-xs active:scale-95 transition"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving Changes…" : "Save Store Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}

